
import React from 'react';
import * as Sentry from '@sentry/react';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';

interface FallbackProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError(): void;
}

// This component will be displayed when an error occurs
const ErrorFallback = ({ 
  error, 
  resetError, 
  eventId 
}: FallbackProps) => {
  const navigate = useNavigate();
  
  const handleGoHome = () => {
    resetError();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full p-6 bg-white shadow-lg rounded-lg">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Something went wrong
          </h2>
          <div className="text-gray-700 mb-6">
            <p className="mb-4">
              We're sorry, but something unexpected happened. Our team has been notified.
            </p>
            
            {eventId && (
              <p className="text-xs text-gray-500 mb-2">
                Error ID: {eventId}
              </p>
            )}
            
            <div className="rounded-md bg-red-50 p-4 border border-red-100 mb-4">
              <p className="text-sm font-medium text-red-800">
                {error.message || 'Unknown error'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              onClick={resetError} 
              className="w-full"
            >
              Try again
            </Button>
            
            <Button
              onClick={handleGoHome}
              className="w-full"
            >
              Go back home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// The ErrorBoundary wrapper component - fixed to use the proper fallback approach
export const SentryErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <ErrorFallback 
          error={error} 
          componentStack={componentStack} 
          eventId={eventId} 
          resetError={resetError} 
        />
      )}
      showDialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
